/**
 * The subset of bulk-editable location fields the assistant can set, mirroring
 * `BulkLocationEditField` on the backend (the single source of truth for the
 * export's column names).
 *
 * Deliberately excluded even though the backend allows them:
 *  - `location_name`, because renaming the location the user is currently
 *    chatting about is more likely a misparse than an intent.
 */

import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';

export type FieldValueType = 'TEXT' | 'INTEGER' | 'BOOLEAN' | 'MONEY';

export interface EditableFieldDef {
  /** CSV column name, matching the backend enum exactly. */
  column: string;
  /** How the field is referred to back to the user. */
  label: string;
  type: FieldValueType;
  /** Phrases a user might use. Matched longest-first, so order here is irrelevant. */
  synonyms: string[];
}

export const LOCATION_DESCRIPTION_COLUMN = 'location_description';

export const EDITABLE_FIELDS: EditableFieldDef[] = [
  {
    column: 'total_number_of_spaces',
    label: 'total number of spaces',
    type: 'INTEGER',
    // No bare "spaces" synonym: it is ambiguous between the total, minimum and
    // auto-approve counts, and as a catch-all it silently swallowed qualified
    // phrasings like "auto approve spaces" into the wrong column.
    synonyms: ['total number of spaces', 'total spaces', 'number of spaces', 'capacity'],
  },
  {
    column: 'min_number_of_spaces',
    label: 'minimum number of spaces',
    type: 'INTEGER',
    synonyms: ['minimum number of spaces', 'min number of spaces', 'minimum spaces', 'min spaces'],
  },
  {
    column: 'managed_spaces',
    label: 'auto-approve spaces',
    type: 'INTEGER',
    // "Auto-Approve Spaces" is what the UI calls this field; managed_spaces is
    // the backend name and drives instant-approval availability.
    synonyms: [
      'auto approve spaces',
      'auto-approve spaces',
      'auto approved spaces',
      'auto-approved spaces',
      'automatically approved spaces',
      'instant approval spaces',
      'auto approval spaces',
      'managed spaces',
      // Longer than the bare "capacity" synonym, so these win over the total.
      'auto approve capacity',
      'auto-approve capacity',
      'instant approval capacity',
      'managed capacity',
    ],
  },
  {
    column: 'managed_location',
    label: 'automated approvals',
    type: 'BOOLEAN',
    // The UI calls this "Automated Management"; enabling it also requires an
    // auto-approve space count, which the server checks and reports in the
    // preview rather than failing at save time.
    synonyms: [
      'automated approvals',
      'automated approval',
      'automatic approvals',
      'automatic approval',
      'automated management',
      'automatic management',
      'auto approvals',
      'auto approval',
      'auto-approvals',
      'auto-approval',
      'instant approvals',
      'instant approval',
      'managed location',
    ],
  },
  {
    column: 'price_per_day',
    label: 'price per day',
    type: 'MONEY',
    synonyms: ['price per day', 'daily price', 'daily rate', 'day rate'],
  },
  {
    column: 'price_per_week',
    label: 'price per week',
    type: 'MONEY',
    synonyms: ['price per week', 'weekly price', 'weekly rate', 'week rate'],
  },
  {
    column: 'price_per_month',
    label: 'price per month',
    type: 'MONEY',
    synonyms: ['price per month', 'monthly price', 'monthly rate', 'month rate'],
  },
  {
    column: 'overage_rate',
    label: 'overage rate',
    type: 'MONEY',
    synonyms: ['overage rate'],
  },
  {
    column: 'overage_grace_period_in_minutes',
    label: 'overage grace period (minutes)',
    type: 'INTEGER',
    synonyms: ['overage grace period', 'grace period'],
  },
  {
    column: 'charge_overages',
    label: 'charge overages',
    type: 'BOOLEAN',
    synonyms: ['charge overages', 'overage charging', 'overage charges', 'overages'],
  },
  {
    column: 'qr_scanner_available',
    label: 'QR scanner available',
    type: 'BOOLEAN',
    synonyms: ['qr scanner available', 'qr scanner', 'qr code scanner'],
  },
  {
    column: 'phone_number',
    label: 'phone number',
    type: 'TEXT',
    synonyms: ['phone number', 'phone'],
  },
  {
    column: 'gate_hours',
    label: 'gate hours',
    type: 'TEXT',
    synonyms: ['gate hours'],
  },
  {
    column: 'instructions',
    label: 'instructions',
    type: 'TEXT',
    synonyms: ['instructions', 'gate instructions'],
  },
  {
    column: 'after_hours_access_procedure',
    label: 'after-hours access procedure',
    type: 'TEXT',
    synonyms: [
      'after hours access procedure',
      'after-hours access procedure',
      'after hours access',
      'after-hours access',
      'after hours procedure',
    ],
  },
  {
    column: 'location_manager_name',
    label: 'location manager name',
    type: 'TEXT',
    synonyms: ['location manager name', 'manager name'],
  },
  {
    column: 'location_manager_phone',
    label: 'location manager phone',
    type: 'TEXT',
    synonyms: ['location manager phone', 'manager phone'],
  },
  {
    column: 'location_manager_email',
    label: 'location manager email',
    type: 'TEXT',
    synonyms: ['location manager email', 'manager email'],
  },
  {
    column: LOCATION_DESCRIPTION_COLUMN,
    label: 'description',
    type: 'TEXT',
    synonyms: ['description'],
  },
];

/** Flattened (synonym, field) pairs sorted longest-first. */
const SYNONYM_INDEX: { synonym: string; field: EditableFieldDef }[] = EDITABLE_FIELDS.flatMap(
  (field) => field.synonyms.map((synonym) => ({ synonym, field })),
).sort((a, b) => b.synonym.length - a.synonym.length);

/**
 * Finds the field a message refers to.
 *
 * Longest synonym wins, so "minimum number of spaces" resolves to
 * `min_number_of_spaces` rather than matching the shorter "number of spaces",
 * and "manager phone" beats a bare "phone".
 */
export const resolveField = (text: string): EditableFieldDef | null => {
  const lower = text.toLowerCase();
  const match = SYNONYM_INDEX.find((entry) => lower.includes(entry.synonym));
  return match ? match.field : null;
};

export const findFieldByColumn = (column: string): EditableFieldDef | undefined =>
  EDITABLE_FIELDS.find((field) => field.column === column);

export interface ParsedValue {
  value: string | null;
  error: string | null;
}

/**
 * Polarity stated directly against a verb ("turn off", "set it to on").
 *
 * Checked before the bare keywords below because "on" is also a preposition in
 * this grammar — "turn off the qr scanner **on** Cheyenne" must read as *off*,
 * not *on*. Anchoring to the verb removes that collision.
 */
const EXPLICIT_POLARITY = /\b(?:turn|turned|switch|toggle|flip|set|to)\s+(?:it\s+|them\s+|this\s+)?(on|off)\b/i;

/** Unambiguous standalone words. Bare "on" is deliberately absent — see above. */
const TRUTHY = /\b(true|yes|enable|enabled|activate|activated|allow)\b/;
const FALSY = /\b(false|no|off|disable|disabled|deactivate|deactivated|stop)\b/;

/**
 * Extracts the numeric token the user intends as the value.
 *
 * Prefers the number introduced by "to" ("set capacity to 1,433"), which avoids
 * picking up an unrelated figure earlier in the sentence (including digits in a
 * location name like "Lot 45"), and falls back to the last number present.
 */
const extractNumericToken = (text: string): string | null => {
  const introduced = text.match(/\bto\s+\$?\s*([\d,]+(?:\.\d+)?)/i);
  if (introduced) return introduced[1];
  const all = text.match(/\$?\s*[\d,]+(?:\.\d+)?/g);
  return all ? all[all.length - 1] : null;
};

/**
 * Converts a dollar amount to the integer cents the API stores.
 *
 * Prices round-trip through the API in cents (`formatMoneyAddDecimalPointAndThousands`
 * divides by 100), so a user saying "25" means $25.00 / 2500 — passing 25 straight
 * through would silently set the price to a quarter.
 *
 * Done with string arithmetic rather than `* 100` to avoid binary float error.
 */
const dollarsToCents = (cleaned: string): number => {
  const [dollars, fraction = ''] = cleaned.split('.');
  const paddedFraction = `${fraction}00`.slice(0, 2);
  return Number(dollars) * 100 + Number(paddedFraction);
};

/**
 * Extracts the value for a field from the user's message.
 *
 * Money fields are read as dollars and stored as cents; plain integers are passed
 * through verbatim. Currency symbols and thousands separators are stripped.
 */
export const parseValueForField = (field: EditableFieldDef, text: string): ParsedValue => {
  if (field.type === 'MONEY') {
    const raw = extractNumericToken(text);
    if (!raw) {
      return { value: null, error: `I could not find an amount to set the ${field.label} to.` };
    }

    const cleaned = raw.replace(/[$,\s]/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
      return {
        value: null,
        error: `"${raw.trim()}" is not an amount I can use for the ${field.label} — use dollars, like 25 or 25.50.`,
      };
    }
    return { value: String(dollarsToCents(cleaned)), error: null };
  }

  if (field.type === 'INTEGER') {
    const raw = extractNumericToken(text);

    if (!raw) {
      return { value: null, error: `I could not find a number to set the ${field.label} to.` };
    }

    const cleaned = raw.replace(/[$,\s]/g, '');
    const numeric = Number(cleaned);
    if (!Number.isFinite(numeric)) {
      return { value: null, error: `"${raw.trim()}" is not a number I can use for the ${field.label}.` };
    }
    if (!Number.isInteger(numeric)) {
      return { value: null, error: `The ${field.label} has to be a whole number, but I read "${cleaned}".` };
    }
    if (numeric < 0) {
      return { value: null, error: `The ${field.label} cannot be negative.` };
    }
    return { value: String(numeric), error: null };
  }

  if (field.type === 'BOOLEAN') {
    const explicit = text.match(EXPLICIT_POLARITY);
    if (explicit) {
      return { value: explicit[1].toLowerCase() === 'on' ? 'true' : 'false', error: null };
    }

    const lower = text.toLowerCase();
    if (TRUTHY.test(lower)) return { value: 'true', error: null };
    if (FALSY.test(lower)) return { value: 'false', error: null };
    return { value: null, error: `Should ${field.label} be on or off?` };
  }

  const quoted = text.match(/"([^"]+)"|'([^']+)'|\u201c([^\u201d]+)\u201d/);
  const captured = quoted ? quoted[1] ?? quoted[2] ?? quoted[3] : null;
  if (!captured || !captured.trim()) {
    return {
      value: null,
      error: `Please put the new ${field.label} in quotes so I set it word for word.`,
    };
  }
  return { value: captured.trim(), error: null };
};

/** Turns a raw column name into something readable if it isn't in the registry. */
const humanizeColumn = (column: string): string =>
  column.replace(/_/g, ' ').replace(/\bin minutes\b/, '(minutes)').trim();

/**
 * The user-facing name for a column.
 *
 * Users never see the CSV column names anywhere else in the product, so the diff
 * shows the registry label (matching the wording used elsewhere in the UI).
 */
export const fieldDisplayLabel = (column: string): string => {
  const label = findFieldByColumn(column)?.label ?? humanizeColumn(column);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Formats a value from the preview for display.
 *
 * Money columns arrive as integer cents, which read as absurd numbers ("2300")
 * next to the dollar amount the user typed, so they are rendered as currency.
 */
export const formatFieldValue = (column: string, value: string | null): string | null => {
  if (value === null || value === '') return value;

  if (findFieldByColumn(column)?.type === 'MONEY') {
    const cents = Number(value);
    if (Number.isFinite(cents)) return formatMoneyAddDecimalPointAndThousands(cents);
  }
  return value;
};
